// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * ProtectedRoute Component
 *
 * Three states, not two. MSAL React reports "not authenticated" on the first
 * render after every page load, while it is still starting up — the account
 * is in the cache, it just hasn't been read yet. Treating that as "logged
 * out" used to swap every refreshed URL for `/login` and rely on router
 * state to find the way back, which a CIAM redirect or a second refresh
 * during the bounce would lose (landing the user on their persona home).
 *
 * So: while MSAL is busy, hold the URL and show a loading state. Only once it
 * has settled with no account is the visitor really signed out; then send
 * them to `/login` with the full location in `?next=`.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Loading } from '../shell/ScreenHelpers';
import { loginPathFor } from '../../utils/returnPath';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  if (isAuthenticated) return <>{children}</>;
  if (inProgress !== InteractionStatus.None) return <Loading label="Signing you in…" />;

  return <Navigate to={loginPathFor(location)} replace />;
}

export default ProtectedRoute;
